/**
 * Retenues sur le CA des abonnements : lecture et saisie.
 *
 * Chaque écriture renvoie la liste entière : on la pose directement dans le
 * cache plutôt que de relire.
 */

import type { SubscriptionDeduction, SubscriptionDeductionInput } from '@shared/types/settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, createDeduction, deleteDeduction, fetchDeductions, updateDeduction } from '../lib/api';
import { useAuth } from './useAuth';

const DEDUCTIONS_KEY = ['subscription-deductions'] as const;

export function useDeductions() {
  const { token, signOut } = useAuth();
  return useQuery<SubscriptionDeduction[], Error>({
    queryKey: DEDUCTIONS_KEY,
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return (await fetchDeductions(token, signal)).deductions;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && failureCount < 2,
  });
}

type DeductionWrite =
  | { kind: 'create'; input: SubscriptionDeductionInput }
  | { kind: 'update'; id: number; input: SubscriptionDeductionInput }
  | { kind: 'delete'; id: number };

export function useWriteDeduction() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<SubscriptionDeduction[], Error, DeductionWrite>({
    mutationFn: async (write) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      const response =
        write.kind === 'create'
          ? await createDeduction(write.input, token)
          : write.kind === 'update'
            ? await updateDeduction(write.id, write.input, token)
            : await deleteDeduction(write.id, token);
      return response.deductions;
    },
    onSuccess: (deductions) => {
      queryClient.setQueryData(DEDUCTIONS_KEY, deductions);
    },
  });
}
